import { Component, ViewChild, signal, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { merge, firstValueFrom, Observable } from "rxjs";
import { map, startWith, switchMap } from "rxjs/operators";
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from "@angular/material/table";
import { MatSort, MatSortHeader } from "@angular/material/sort";
import { timer } from "rxjs";
import { MatPaginator } from "@angular/material/paginator";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Subscription } from "rxjs";
import { ConfirmationDialog } from "../incs/confirmation-dialog.component";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { ValidationService } from "../../service/validation.service";
import { AbstractAdvisorComponent } from "../abstract/advisor.component";
import { ClientData } from "../../obj/ClientData";
import { User } from "../../entity/User";
import { State } from "../../entity/State";
import { HeaderComponent } from "../incs/header.component";
import { DatePipe } from "@angular/common";
import { NgxMaskDirective } from "ngx-mask";
import { Company } from "../../entity/Company";
import { Pair } from "../../obj/Pair";
import { ContactManagerDialog } from "../incs/contact-manager-dialog.component";


@Component({
    selector: "kilowant-client-list",
    templateUrl: "../../../html/advisor/main-client-list.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [HeaderComponent, FormsModule, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatSortHeader, NgxMaskDirective, ReactiveFormsModule, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, DatePipe]
})
export class ClientListComponent extends AbstractAdvisorComponent {
    clientsSource: MatTableDataSource<any>;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    clientListSubscription: Subscription | null;

    total: number = 0;
    currentStart: number = 0;

    currentPhoneNumber: FormControl = new FormControl("");

    countDown: Subscription;

    dialogRef: MatDialogRef<ConfirmationDialog> = null!;
    displayedColumns: string[] = ["id", "ClientName", "AccountCount", "NextAvailableDate"];

    fragment: string | null;
    states: State[] = [];
    clients: ClientData[] = [];

    // Converted cities to a signal
    private _cities = signal<string[]>([]);
    get cities(): string[] {
        return this._cities();
    }
    set cities(value: string[]) {
        this._cities.set(value);
    }

    // Converted client to a signal
    client: User | null = null;
    clientBackup: User | null = null;
    editModePos: number = -1;
    fileToUpload: File | null = null;
    deleteTaxExemptCertificateDocument: boolean;

    viewClientsType: string = "all";
    viewActiveType: string = "all";

    keywords: string | null = null;

    isResidential: boolean = false;

    clientType: string = "";
    kilowantAccess: number = 0;
    editContact: User;
    externalContact: Pair | null;
    clientEditDisabled = signal<boolean>(false);
    contactManagers: Pair[];


    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        this.generalService.getStates(false).then((states: any) => {
            this.states = states;
        });
        this.route.fragment.subscribe((fragment: string | null) => {
            this.fragment = fragment;
        });
        firstValueFrom(this.generalService.getContactManagers()).then((x: any) => { this.contactManagers = x; });
    }

    ngAfterViewInit() {
        this.refreshClients();
    }

    refreshClients(): void {
        let isResidential: boolean | null = null;
        if (this.viewClientsType == "residential") {
            isResidential = true;
        }
        if (this.viewClientsType == "commercial") {
            isResidential = false;
        }
        if (this.clientListSubscription != null) {
            this.clientListSubscription.unsubscribe();
            this.clientListSubscription = null;
        }
        this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.clientListSubscription = merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                startWith({}),
                switchMap(() => {
                    let viewType = null;
                    if (this.viewActiveType == "active") {
                        viewType = true;
                    }
                    if (this.viewActiveType == "inactive") {
                        viewType = false;
                    }
                    return this.generalService.getClients(isResidential, viewType, this.keywords, this.total, this.paginator.pageIndex, this.paginator.pageSize, this.sort.active, (this.sort.direction == "desc"));
                }),
                map(data => {
                    if (data === null) {
                        return [];
                    }
                    if (data.key != 0 && this.total != data.key) {
                        this.total = data.key;
                    }
                    this.currentStart = this.paginator.pageIndex * this.paginator.pageSize;
                    return data.value;
                }),
            )
            .subscribe(data => { this.clients = data; this.cdr.markForCheck(); });
    }

    addClient(): void {
        this.router.navigate(["/client/add"]);
    }

    downloadReport(): void {
        var a = document.createElement("a");
        document.body.appendChild(a);
        this.generalService.getAdvisorReportUsersAccounts()
            .subscribe((data: any) => {
                var blob = new Blob([data], { type: "octet/stream" });
                var url = window.URL.createObjectURL(blob);
                a.href = url;
                a.download = "kilowant-accounts.csv";
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            });
    }

    editClient(id: number): void {
        if (this.client != null && this.client.id == id) {
            this.cancel();
            return;
        }
        firstValueFrom(this.generalService.getClient(id))
            .then(
                (res: any) => {
                    this.client = res.key;
                    if (res.value == null) {
                        this.externalContact = null;
                        this.clientEditDisabled.set(false);
                    } else {
                        this.externalContact = new Pair(res.value.id, res.value.firstName + ' ' + res.value.lastName);
                        this.setEditContactBasedOnLink();
                    }
                    this.kilowantAccess = (this.externalContact == null) ? 1 : 2;
                    if (this.externalContact != null) {
                        this.clientEditDisabled.set(true);
                    }
                    if (this.client != null) {
                        this.editContact = this.client;
                        this.clientBackup = new User(this.client.id);
                        this.clientBackup.firstName = this.client.firstName;
                        this.clientBackup.lastName = this.client.lastName;
                        this.fileToUpload = null;
                        this.currentPhoneNumber.setValue(this.client.phone);
                        this.clearErrors();
                        if (this.client.company != null) {
                            this.clientType = "Commercial";
                        } else {
                            this.clientType = "Residential";
                        }
                        if (this.client!.alias == null || this.client!.alias.length == 0) {
                            this.client!.alias = this.client.firstName + " " + this.client.lastName;
                            if (this.client.company != null) {                                
                                this.client!.alias = this.client.company.name;
                            }
                        }
                        this.refreshView();
                    }
                }
            );
    }

    deleteClient(client: User): void {
        this.dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false
            , panelClass: "i-icon-popup"
        });
        this.dialogRef.componentInstance.confirmMessage = "Are you sure you want to delete this client?"
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.generalService.deleteClient(client.id)
                    .subscribe(
                        {
                            next: (res: any) => {
                                if (res) {
                                    this.total = 0;
                                    this.generalService.clearStoredClient();
                                    this.refreshClients();
                                }
                            },
                            error: (err: any) => {
                                if (err.status == 400) {
                                    this.setErrorText(err.error);
                                }
                            }
                        });
            }
            this.dialogRef = null!;
        });
    }

    setClientSelfManaged(client: User): void {
        this.dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false
            , panelClass: "i-icon-popup"
        });
        this.dialogRef.componentInstance.confirmMessage = "Are you sure you want to set this client as self-managed?"
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.generalService.setClientSelfManaged(client.id)
                    .subscribe(
                        {
                            next: (res: any) => {
                                if (res) {
                                    this.total = 0;
                                    this.generalService.clearStoredClient();
                                    this.refreshClients();
                                }
                            },
                            error: (err: any) => {
                                if (err.status == 400) {
                                    this.setErrorText(err.error);
                                }
                            }
                        });
            }
            this.dialogRef = null!;
        });
    }    

    searchSiteInputChanged(event: any): void {
        if (this.countDown) {
            this.countDown.unsubscribe();
        }
        this.countDown = timer(500).subscribe((count) => {
            this.total = 0;
            this.paginator.pageIndex = 0;
            this.refreshClients();
        });
    }

    cancel(): void {
        this.client = null;
        this.fileToUpload = null;
        this.clearErrors();
    }

    updateClient(): void {
        this.clearErrors();
        if (this.client != null) {
            if (this.client.alias == null || this.client.alias.length == 0) {
                this.addError("ClientName");
            }
            if (this.externalContact == null) {
                if (this.client.firstName == null || this.client.firstName.length == 0) {
                    this.addError("FirstName");
                }
                if (this.client.lastName == null || this.client.lastName.length == 0) {
                    this.addError("LastName");
                }
                if (this.client.email == null || this.client.email.length == 0) {
                    this.addError("Email");
                }
            }
            if (this.client.addressLine1 == null || this.client.addressLine1 == "") {
                this.addError("addressLine1");
            }
            if (this.client.zip == null || this.client.zip == "") {
                this.addError("zip");
            }
            if (this.client.company != null) {
                this.client.company.name = this.client.alias;
            }
            if (this.client.company != null && this.client.company.taxExempt == true && (this.client.company.federalTaxID == null || this.client.company.federalTaxID.length == 0)) {
                this.addError("FederalTaxID");
            }
            if (this.client.company != null && this.client.company.taxExempt == true && this.fileToUpload == null && this.client.company.taxExemptCertificateDocument == null) {
                this.addError("fileToUpload");
            }
            if (this.errorY().showError()) {
                this.setErrorText("Please enter the data requested above.");
                return;
            }

            if (this.client.city == null || this.client.city.length == 0 || this.client.state == null) {
                this.addError("zip");
                this.setErrorText("Zip code is not correct.");
                return;
            }

            if (this.externalContact == null && !this.currentPhoneNumber.valid) {
                this.addError("Phone");
                this.setErrorText("Phone number is not correct.");
                return;
            }
            this.client.phone = this.currentPhoneNumber.value;

            this.generalService.updateUser(this.client, (this.externalContact==null)?0:this.externalContact.key, this.fileToUpload, this.deleteTaxExemptCertificateDocument, true).subscribe(
                {
                    next: (res: any) => {
                        if (res) {
                            if (!this.client?.isActive) {
                                this.generalService.clearStoredClient();
                            }
                            this.client = null;
                            this.fileToUpload = null;
                            this.clearErrors();
                            this.refreshClients();
                        }
                    },
                    error: (err) => {
                        if (err.status == 400) {
                            this.setErrorText(err.error);
                        }
                    }
                }                
            );
        }
    }

    changeClientView(): void {
        this.total = 0;
        this.refreshClients();
    }

    changeClientContact(): void {
        if (this.kilowantAccess == 1) {
            this.externalContact = null;
            this.editContact = this.client!;
            this.currentPhoneNumber.setValue(this.client!.phone);
            this.clientEditDisabled.set(false);        
        } else {
            this.resetError("FirstName");
            this.resetError("LastName");
            this.resetError("Email");
            this.resetError("Phone");
            this.externalContact = this.contactManagers[0];
            this.changeExternalContact();
        }
        this.refreshView();
    }

    changeExternalContact(): void {
        if (this.externalContact != null && this.externalContact.key > 0) {
            this.setEditContactBasedOnLink();
        } else {
            this.clientEditDisabled.set(false);
        }
        if (this.externalContact == null) {
            this.addUpdateContactManager();
        }
        this.refreshView();
    }

    setEditContactBasedOnLink(): void {
        if (this.externalContact != null && this.externalContact.key > 0) {
            firstValueFrom(this.generalService.getClient(this.externalContact.key))
                .then((res: any) => {
                    this.editContact = res.key;
                    this.currentPhoneNumber.setValue(res.key.phone);
                    this.refreshView();
                });
            this.clientEditDisabled.set(true);
        }
    }

    getStateName(state: State | null): string {
        if (state == null) {
            return "";
        }
        return state.stateCode;
    }

    zipChanged(): void {
        this.resetError('zip');
        if (this.client != null) {
            this.client.city = "";
            this.client.state = null;
            if (this.client.zip != null && this.client.zip?.length > 2) {
                let request = this.generalService.getStateCityByZip(this.client.zip);
                if (request == null) {
                    return;
                }
                firstValueFrom(request).then(data => {
                    if (data != null) {
                        this.client!.state = data.key;
                        this.client!.city = data.value;
                        this.refreshView();
                    }
                });
            }
        }
    }

    downloadFile(ifExisting: boolean, id: number | null) {
        if (!ifExisting) {
            this.generalService.sendFileToClient(this.fileToUpload);
        } else if (id != null) {
            this.generalService.openDocument(id);
        }
    }

    deleteFile() {
        this.fileToUpload = null;
    }

    handleFileInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.fileToUpload = (target.files as FileList).item(0) as File;
        this.resetError("fileToUpload");
    }

    deleteExistingFile(key: number) {
        this.deleteTaxExemptCertificateDocument = true;
        if (this.client != null && this.client.company != null) {
            this.client.company.taxExemptCertificateDocument = null!;
        }
    }

    switchClientType(): void {
        if (this.clientType == "Commercial" && this.client!.company == null) {
            this.client!.company = new Company();
        }
        if (this.clientType == "Residential") {
            this.client!.company = null;
        }
    }

    ngOnDestroy() {
        if (this.clientListSubscription != null) {
            this.clientListSubscription.unsubscribe();
        }
        if (this.countDown != null) {
            this.countDown.unsubscribe();
        }
    }

    addUpdateContactManager(): void {
        var dialogRef = this.dialog.open(ContactManagerDialog, {
            disableClose: false,
            panelClass: "card-popup"
        });
        if (this.externalContact != null) {
            dialogRef.componentInstance.loadContact(this.externalContact.key);
        }
        dialogRef.afterClosed().subscribe(result => {
            if (result > 0) {
                this.externalContact = new Pair(result, this.externalContact!.value);
            }
            if (this.externalContact == null && this.kilowantAccess == 2) {
                this.kilowantAccess = 1;
                this.changeClientContact();
            } else {
                this.changeExternalContact();
                firstValueFrom(this.generalService.getContactManagers()).then((x: any) => { this.contactManagers = x; });
            }
        });
    }
}
